import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, X, Star, Megaphone } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, ShoreBadge, EmptyState } from "@/components/shared";

const TABS = [
  { key: "listings", label: "Listings" },
  { key: "tourism", label: "Tourism" },
  { key: "introductions", label: "Introductions" },
  { key: "enquiries", label: "Enquiries" },
  { key: "contacts", label: "Messages" },
];

export default function Admin() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [tourism, setTourism] = useState([]);
  const [intros, setIntros] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) navigate("/signin");
  }, [ready, user, navigate]);

  const load = () => {
    Promise.all([
      api.get("/admin/listings").then((r) => setListings(r.data)),
      api.get("/admin/tourism").then((r) => setTourism(r.data)),
      api.get("/admin/introductions").then((r) => setIntros(r.data)),
      api.get("/admin/enquiries").then((r) => setEnquiries(r.data)),
      api.get("/admin/contacts").then((r) => setContacts(r.data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  const patch = async (id, body, msg) => {
    try {
      const { data } = await api.put(`/admin/listings/${id}`, body);
      setListings((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const patchTourism = async (id, body, msg) => {
    try {
      const { data } = await api.put(`/admin/tourism/${id}`, body);
      setTourism((ts) => ts.map((t) => (t.id === id ? data : t)));
      toast.success(msg);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (!ready || !user || user.role !== "admin") {
    return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;
  }

  const pendingCount = listings.filter((l) => l.status === "pending").length;

  return (
    <div>
      <section className="bg-[#1E1E2F] text-[#FFFAF3] border-b border-[#D6D3CB]">
        <Container className="py-12">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-[#CDA434] mb-2">Moderation</p>
          <h1 className="font-serif text-3xl">The harbour master's desk</h1>
          <p className="font-sans text-sm text-[#FFFAF3]/70 mt-1">
            {pendingCount} listing{pendingCount === 1 ? "" : "s"} awaiting approval.
          </p>
        </Container>
      </section>

      <section className="py-10">
        <Container>
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#D6D3CB]">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`font-sans text-sm px-4 py-3 -mb-px border-b-2 transition-colors ${
                  tab === t.key ? "border-[#CDA434] text-[#003366] font-semibold" : "border-transparent text-[#505A68] hover:text-[#003366]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="h-40 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />
          ) : (
            <>
              {tab === "listings" && (
                <div className="space-y-4" data-testid="admin-listings">
                  {listings.map((l) => (
                    <div key={l.id} data-testid={`admin-listing-${l.slug}`} className="bg-[#FFFAF3] border border-[#D6D3CB] p-5 flex flex-col sm:flex-row gap-4">
                      <img src={l.image_url || "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt={l.name} className="w-24 h-24 object-cover border border-[#D6D3CB] shrink-0" />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-serif text-lg text-[#1E1E2F]">{l.name}</h3>
                          <ShoreBadge shore={l.shore} />
                          <span className={`text-xs font-semibold uppercase px-2 py-0.5 border ${
                            l.status === "live" ? "text-[#003366] border-[#003366]/30 bg-[#003366]/10" :
                            l.status === "pending" ? "text-[#7a611c] border-[#CDA434]/50 bg-[#CDA434]/15" :
                            "text-[#505A68] border-[#D6D3CB]"
                          }`}>{l.status}</span>
                          {l.featured && <span className="text-xs text-[#7a611c] flex items-center gap-1"><Star size={12} /> featured</span>}
                          {l.advert && <span className="text-xs text-[#505A68] flex items-center gap-1"><Megaphone size={12} /> advert</span>}
                        </div>
                        <p className="font-sans text-sm text-[#505A68] mb-3">{l.category} · {l.plan} · {l.town}</p>
                        <div className="flex flex-wrap gap-2">
                          {l.status !== "live" && (
                            <button data-testid={`approve-${l.slug}`} onClick={() => patch(l.id, { status: "live" }, "Listing approved and now live.")}
                              className="inline-flex items-center gap-1 text-xs font-semibold bg-[#003366] text-[#FFFAF3] px-3 py-1.5 hover:bg-[#00284d]">
                              <Check size={13} /> Approve
                            </button>
                          )}
                          {l.status !== "rejected" && (
                            <button data-testid={`reject-${l.slug}`} onClick={() => patch(l.id, { status: "rejected" }, "Listing rejected.")}
                              className="inline-flex items-center gap-1 text-xs font-semibold border border-[#D6D3CB] text-[#505A68] px-3 py-1.5 hover:border-[#003366]">
                              <X size={13} /> Reject
                            </button>
                          )}
                          <button data-testid={`toggle-featured-${l.slug}`} onClick={() => patch(l.id, { featured: !l.featured }, l.featured ? "Featured off." : "Featured on.")}
                            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border ${l.featured ? "bg-[#CDA434] text-[#1E1E2F] border-[#CDA434]" : "border-[#D6D3CB] text-[#505A68] hover:border-[#CDA434]"}`}>
                            <Star size={13} /> Featured
                          </button>
                          <button data-testid={`toggle-advert-${l.slug}`} onClick={() => patch(l.id, { advert: !l.advert }, l.advert ? "Advert off." : "Advert on.")}
                            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border ${l.advert ? "bg-[#1E1E2F] text-[#FFFAF3] border-[#1E1E2F]" : "border-[#D6D3CB] text-[#505A68] hover:border-[#1E1E2F]"}`}>
                            <Megaphone size={13} /> Advert
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === "tourism" && (
                <div className="space-y-4" data-testid="admin-tourism">
                  {tourism.map((t) => (
                    <div key={t.id} data-testid={`admin-tourism-${t.id}`} className="bg-[#FFFAF3] border border-[#D6D3CB] p-5 flex flex-col sm:flex-row gap-4">
                      <img src={t.image_url || "https://images.pexels.com/photos/8755712/pexels-photo-8755712.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"} alt={t.name} className="w-24 h-24 object-cover border border-[#D6D3CB] shrink-0" />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-serif text-lg text-[#1E1E2F]">{t.name}</h3>
                          <ShoreBadge shore={t.shore} />
                          <span className={`text-xs font-semibold uppercase px-2 py-0.5 border ${
                            t.status === "live" ? "text-[#003366] border-[#003366]/30 bg-[#003366]/10" :
                            t.status === "pending" ? "text-[#7a611c] border-[#CDA434]/50 bg-[#CDA434]/15" :
                            "text-[#505A68] border-[#D6D3CB]"
                          }`}>{t.status}</span>
                          {t.paid && <span className="text-xs text-[#7a611c]">Subscribed</span>}
                        </div>
                        <p className="font-sans text-sm text-[#505A68] mb-3">{t.category} · {t.town}</p>
                        <div className="flex flex-wrap gap-2">
                          {t.status !== "live" && (
                            <button data-testid={`tourism-approve-${t.id}`} onClick={() => patchTourism(t.id, { status: "live" }, "Tourism listing approved.")}
                              className="inline-flex items-center gap-1 text-xs font-semibold bg-[#003366] text-[#FFFAF3] px-3 py-1.5 hover:bg-[#00284d]"><Check size={13} /> Approve</button>
                          )}
                          {t.status !== "rejected" && (
                            <button data-testid={`tourism-reject-${t.id}`} onClick={() => patchTourism(t.id, { status: "rejected" }, "Tourism listing rejected.")}
                              className="inline-flex items-center gap-1 text-xs font-semibold border border-[#D6D3CB] text-[#505A68] px-3 py-1.5 hover:border-[#003366]"><X size={13} /> Reject</button>
                          )}
                          <button data-testid={`tourism-featured-${t.id}`} onClick={() => patchTourism(t.id, { featured: !t.featured }, t.featured ? "Featured off." : "Featured on.")}
                            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 border ${t.featured ? "bg-[#CDA434] text-[#1E1E2F] border-[#CDA434]" : "border-[#D6D3CB] text-[#505A68] hover:border-[#CDA434]"}`}><Star size={13} /> Featured</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === "introductions" && (
                <RecordList
                  items={intros}
                  testId="admin-introductions"
                  empty="No introductions requested yet."
                  render={(x) => (
                    <>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-serif text-base text-[#1E1E2F]">{x.name} · <span className="text-[#7a611c] capitalize">{x.intent}</span></span>
                        <span className="text-xs text-[#505A68]">{x.shore}</span>
                      </div>
                      <a href={`mailto:${x.email}`} className="text-xs text-[#003366] link-underline">{x.email}</a>
                      <p className="font-sans text-sm text-[#1B2430] mt-2">{x.message}</p>
                    </>
                  )}
                />
              )}

              {tab === "enquiries" && (
                <RecordList
                  items={enquiries}
                  testId="admin-enquiries"
                  empty="No enquiries received yet."
                  render={(x) => (
                    <>
                      <span className="font-serif text-base text-[#1E1E2F]">{x.from_name}</span>
                      <a href={`mailto:${x.from_email}`} className="block text-xs text-[#003366] link-underline">{x.from_email}</a>
                      <p className="font-sans text-sm text-[#1B2430] mt-2">{x.message}</p>
                    </>
                  )}
                />
              )}

              {tab === "contacts" && (
                <RecordList
                  items={contacts}
                  testId="admin-contacts"
                  empty="No messages yet."
                  render={(x) => (
                    <>
                      <span className="font-serif text-base text-[#1E1E2F]">{x.name}</span>
                      <a href={`mailto:${x.email}`} className="block text-xs text-[#003366] link-underline">{x.email}</a>
                      <p className="font-sans text-sm text-[#1B2430] mt-2">{x.message}</p>
                    </>
                  )}
                />
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
}

function RecordList({ items, render, empty, testId }) {
  if (!items.length) return <EmptyState heading="Nothing here yet" text={empty} />;
  return (
    <div className="space-y-3" data-testid={testId}>
      {items.map((x) => (
        <div key={x.id} className="bg-[#FFFAF3] border border-[#D6D3CB] p-5">
          {render(x)}
          <p className="text-xs text-[#505A68] mt-3">{new Date(x.created_at).toLocaleString("en-GB")}</p>
        </div>
      ))}
    </div>
  );
}
